import React, { useState, useEffect, useCallback } from 'react';
import {
  MdPerson,
  MdAssignment,
  MdRefresh,
  MdSearch,
  MdClose,
  MdLock,
  MdBarChart,
  MdDescription,
  MdArrowUpward,
  MdArrowDownward,
  MdSwapVert
} from 'react-icons/md';
import { hasInstructorPermissions } from '../utils/permissions';
import { useClass } from '../utils/ClassContext';

const ConsentViewer = ({
  username,
  userRole,
  userName,
  apiBaseUrl,
  onNavigate
}) => {
  const [consents, setConsents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const { selectedClass } = useClass();

  // Check if user has permission to view consents
  const hasPermission = hasInstructorPermissions(userRole);

  const fetchConsentData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!selectedClass?.class_id) {
        setError('No class selected.');
        setLoading(false);
        return;
      }

      const response = await fetch(`${apiBaseUrl}/consents`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'User-Role': userRole,
          'Requesting-User': username,
          // Scopes the response to students enrolled in this class - without this
          // the backend previously returned every consent record system-wide.
          'Class-ID': selectedClass.class_id
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setConsents(data.consents || []);
        } else {
          setError(data.error || 'Failed to fetch consent data');
        }
      } else {
        setError('Failed to fetch consent data');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, userRole, username, selectedClass?.class_id]);

  useEffect(() => {
    if (!hasPermission) {
      setLoading(false);
      return;
    }

    // Only fetch if we have valid userRole and username
    if (userRole && username) {
      fetchConsentData();
    } else {
      setLoading(false);
    }
  }, [hasPermission, apiBaseUrl, fetchConsentData, userRole, username, selectedClass?.class_id]);

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort consents based on current sort config
  const sortedConsents = React.useMemo(() => {
    if (!sortConfig.key) return consents;

    return [...consents].sort((a, b) => {
      const aValue = a[sortConfig.key] || '';
      const bValue = b[sortConfig.key] || '';

      if (sortConfig.direction === 'asc') {
        return aValue.toString().localeCompare(bValue.toString());
      } else {
        return bValue.toString().localeCompare(aValue.toString());
      }
    });
  }, [consents, sortConfig]);

  // Filter consents based on search term
  const filteredConsents = React.useMemo(() => {
    if (!searchTerm) return sortedConsents;

    return sortedConsents.filter(consent =>
      Object.values(consent).some(value =>
        value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [sortedConsents, searchTerm]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch {
      return dateString;
    }
  };

  // Get sort indicator
  const getSortIndicator = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <MdSwapVert className="text-gray-400 text-lg" />;
    }
    return sortConfig.direction === 'asc' ?
      <MdArrowUpward className="text-blue-600 text-lg" /> :
      <MdArrowDownward className="text-blue-600 text-lg" />;
  };

  // Render access denied message
  if (!hasPermission) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="text-center py-8">
            <MdLock className="text-6xl text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-4">
              You need instructor privileges to view consent data.
            </p>
            <button
              onClick={() => onNavigate('home')}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-md transition-colors duration-200"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">Consent Data</h1>
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading consent data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">Consent Data</h1>
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center">
              <span className="text-2xl mr-3">❌</span>
              <div>
                <h3 className="text-lg font-medium text-red-800">Error Loading Data</h3>
                <p className="text-red-700">{error}</p>
              </div>
            </div>
            <button
              onClick={fetchConsentData}
              className="mt-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md transition-colors duration-200"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Consent Data</h1>
            <p className="text-gray-600">
              View all research study consent forms submitted by students
            </p>
          </div>
          <div className="hidden md:block">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
              <MdAssignment className="text-white text-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Stats and Controls Card */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <MdBarChart className="text-blue-600 text-2xl" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-800">
                Total Consents: {consents.length}
              </h3>
              <p className="text-sm text-gray-600">
                {filteredConsents.length} shown after filtering
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center w-full sm:w-auto">
            <div className="flex-1 sm:flex-none sm:w-80">
              <label htmlFor="search" className="sr-only">Search consents</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MdSearch className="w-4 h-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="search"
                  placeholder="Search consents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 placeholder-gray-400"
                />
                {searchTerm && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      onClick={() => setSearchTerm('')}
                      className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                      title="Clear search"
                    >
                      <MdClose className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={fetchConsentData}
                className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                title="Refresh consent data"
              >
                <MdRefresh className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Consent Data Table */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800">Consent Records</h2>
        </div>

        {filteredConsents.length === 0 ? (
          <div className="text-center py-12">
            <MdDescription className="text-6xl text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-800 mb-2">
              {consents.length === 0 ? 'No Consent Data Found' : 'No Results Found'}
            </h3>
            <p className="text-gray-600">
              {consents.length === 0
                ? 'No students have submitted consent forms yet.'
                : 'Try adjusting your search criteria.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                    onClick={() => handleSort('username')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Username</span>
                      {getSortIndicator('username')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                    onClick={() => handleSort('fullname')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Full Name</span>
                      {getSortIndicator('fullname')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                    onClick={() => handleSort('session_fullname')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Session Name</span>
                      {getSortIndicator('session_fullname')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                    onClick={() => handleSort('date_of_signature')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Signature Date</span>
                      {getSortIndicator('date_of_signature')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                    onClick={() => handleSort('current_date')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Submitted Date</span>
                      {getSortIndicator('current_date')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredConsents.map((consent, index) => (
                  <tr
                    key={consent.username || index}
                    className="hover:bg-gray-50 transition-colors duration-200"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                          <MdPerson className="text-blue-600 text-sm" />
                        </div>
                        <div className="text-sm font-medium text-gray-900">
                          {consent.username || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {consent.fullname || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {consent.session_fullname || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {consent.date_of_signature || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(consent.current_date)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Footer Stats */}
      {filteredConsents.length > 0 && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="text-center text-sm text-gray-600">
            Showing {filteredConsents.length} of {consents.length} consent records
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsentViewer;